// LinkedIn URL parsing and URN extraction.
//
// Real-world LinkedIn URL shapes we accept:
//   Post:      /posts/<slug>_<text>-activity-<id>-<token>/
//   Post:      /feed/update/urn:li:activity:<id>/
//   Post:      /feed/update/urn:li:share:<id>/           ← company-page / paginated
//   Post:      /feed/update/urn:li:ugcPost:<id>/         ← some org pages
//   Comment:   …?commentUrn=urn:li:comment:(urn:li:activity:POST_ID,COMMENT_ID)       (legacy)
//   Comment:   …?dashCommentUrn=urn:li:fsd_comment:(COMMENT_ID,urn:li:activity:POST_ID) (modern)
//   Reply:     …?dashReplyUrn=urn:li:fsd_reply:(REPLY_ID,COMMENT_ID,urn:li:activity:POST_ID)
//
// Repeat comments-on-company-shares point: the parent can be share/ugcPost
// instead of activity — both variants show up in the wild.

const CONTENT_URN_RE = /(activity|share|ugcPost)[-:](\d{15,25})/i
const COMMENT_URN_RE = /(?:dash(?:Comment|Reply)|comment|reply)Urn=urn:li:(?:fsd_)?(?:comment|reply):\(([^)]+)\)/i
const COMMENT_HINT_RE = /(?:dashComment|dashReply|commentUrn|replyUrn)=|\/comments?\//i

export function extractUrn(url) {
  if (!url || typeof url !== 'string') return null

  // Decode first so the URL-encoded commentUrn payload is parseable. LinkedIn
  // escapes the colons and parens when it's a query-param value; leaving them
  // percent-encoded would force us to write two regexes for every shape.
  let trimmed = url.trim()
  try { trimmed = decodeURIComponent(trimmed) } catch { /* keep raw on malformed URI */ }

  const contentMatch = trimmed.match(CONTENT_URN_RE)
  const parentType = contentMatch?.[1]
  const parentId = contentMatch?.[2]

  const commentMatch = trimmed.match(COMMENT_URN_RE)
  if (commentMatch) {
    const ids = commentMatch[1].match(/\d{15,25}/g) || []
    // The tuple holds (comment_id, parent_post_id) in fsd_{comment,reply}
    // form and (parent_post_id, comment_id) in the legacy commentUrn form.
    // Pick whichever id is NOT the parent content id so we work for both.
    const commentId = ids.find(d => d !== parentId) || ids[0]
    if (commentId) {
      return { type: 'comment', urn: `urn:li:comment:${commentId}`, activityId: parentId || null }
    }
  }

  if (contentMatch) {
    const typeNorm = { activity: 'activity', share: 'share', ugcpost: 'ugcPost' }[parentType.toLowerCase()]
    return { type: 'post', urn: `urn:li:${typeNorm}:${parentId}`, activityId: parentId }
  }

  return null
}

export function isLinkedInUrl(url) {
  if (!url) return false
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`)
    return /(^|\.)linkedin\.com$/i.test(u.hostname) || /(^|\.)lnkd\.in$/i.test(u.hostname)
  } catch {
    return false
  }
}

/**
 * Heuristic: is this URL a comment vs. a post?
 * If the URL contains any comment/reply query param or /comments/ in the
 * path, treat as a comment.
 */
export function detectActivityType(url) {
  if (!url) return null
  return COMMENT_HINT_RE.test(url) ? 'comment' : 'post'
}

/**
 * The LinkedIn share URN format used by Publora: urn:li:share:<id>
 * Activities and shares share the same numeric id in most cases.
 */
export function activityIdToShareUrn(activityId) {
  return `urn:li:share:${activityId}`
}
